import { roundHalfAwayFromZeroAt } from '@digital-twins/deck-ui/foundations/producer-rounding'

/**
 * Display only: re-reads a saved Quick Study plot's printed shares at one
 * decimal, from the unrounded `exactShare` Python also stamps beside every
 * share. Nothing stored changes: counts, `topIds`, order, bases, ranking,
 * heat-grid and histogram plots are left alone, and the input is never mutated.
 *
 * All or nothing per plot: if any printed share has no exact value (a plot
 * saved before 2026-10-18, a percent ranking bar, an opened Other member),
 * the plot is returned untouched and stays at the shares Python stamped, so
 * one plot never mixes precisions and a whole number is never padded.
 */

export const ONE_DECIMAL = 1

interface LoosePlot {
  kind: string
  data: Record<string, unknown>
  alternatives?: unknown
  [key: string]: unknown
}

interface LooseQuestion {
  plots: Record<string, LoosePlot>
  cutPlot?: LoosePlot
  cutPlots?: Array<{ plot: LoosePlot; [key: string]: unknown }>
  [key: string]: unknown
}

function isFigure(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toTenth(exact: number): number {
  return roundHalfAwayFromZeroAt(exact, ONE_DECIMAL)
}

/** Thrown inside a converter when a printed share has no exact value: the plot stays as stamped. */
class MissingExactShare extends Error {}

function exactOf(entry: Record<string, unknown>): number {
  if (!isFigure(entry.exactShare)) {
    throw new MissingExactShare()
  }
  return entry.exactShare
}

function mapEntries(list: unknown, convert: (entry: Record<string, unknown>) => Record<string, unknown>): Array<unknown> {
  if (!Array.isArray(list)) {
    return []
  }
  return list.map((entry) => (isRecord(entry) ? convert(entry) : entry))
}

function withExactShare(entry: Record<string, unknown>, field: string): Record<string, unknown> {
  return { ...entry, [field]: toTenth(exactOf(entry)) }
}

function choiceBarsData(data: Record<string, unknown>): Record<string, unknown> {
  const series = mapEntries(data.series, (one) => ({
    ...one,
    items: mapEntries(one.items, (item) => withExactShare(item, 'share')),
  }))
  return { ...data, series, decimals: ONE_DECIMAL }
}

function shareDonutData(data: Record<string, unknown>): Record<string, unknown> {
  const slices = mapEntries(data.slices, (slice) => withExactShare(slice, 'share'))
  return { ...data, slices, decimals: ONE_DECIMAL }
}

function categoricalData(data: Record<string, unknown>): Record<string, unknown> {
  const rows = mapEntries(data.rows, (row) => {
    const bars = mapEntries(row.bars, (bar) => withExactShare(bar, 'share'))
    const converted: Record<string, unknown> = { ...row, bars }
    if (Array.isArray(row.otherShares)) {
      const exacts = Array.isArray(row.otherExactShares) ? row.otherExactShares : []
      converted.otherShares = row.otherShares.map((share, index) => {
        if (share == null) {
          return share
        }
        const exact = exacts[index]
        if (!isFigure(exact)) {
          throw new MissingExactShare()
        }
        return toTenth(exact)
      })
    }
    return converted
  })
  return { ...data, rows, decimals: ONE_DECIMAL }
}

/** The Top N / Bottom N headline has no exact field: it takes the box's exact share, only when that box rounds to the stamped figure. */
function headlineFromBox(
  stamped: unknown,
  boxSegments: unknown,
  box: 'top' | 'bottom',
  stampedDecimals: number,
): unknown {
  if (!isFigure(stamped)) {
    return stamped
  }
  const found = Array.isArray(boxSegments)
    ? boxSegments.find((piece) => isRecord(piece) && piece.box === box)
    : undefined
  if (!isRecord(found) || !isFigure(found.exactShare)) {
    throw new MissingExactShare()
  }
  if (roundHalfAwayFromZeroAt(found.exactShare, stampedDecimals) !== stamped) {
    throw new MissingExactShare()
  }
  return toTenth(found.exactShare)
}

function divergingLikertData(data: Record<string, unknown>): Record<string, unknown> {
  const stampedDecimals = isFigure(data.decimals) ? data.decimals : 0
  const rows = mapEntries(data.rows, (row) => {
    const converted: Record<string, unknown> = {
      ...row,
      segments: mapEntries(row.segments, (segment) => withExactShare(segment, 'value')),
    }
    if (Array.isArray(row.boxSegments)) {
      converted.boxSegments = mapEntries(row.boxSegments, (piece) => withExactShare(piece, 'value'))
    }
    if (isRecord(row.stats)) {
      converted.stats = {
        ...row.stats,
        topBoxes: headlineFromBox(row.stats.topBoxes, row.boxSegments, 'top', stampedDecimals),
        bottomBoxes: headlineFromBox(row.stats.bottomBoxes, row.boxSegments, 'bottom', stampedDecimals),
      }
    }
    return converted
  })
  // The NPS score is a net score, not a share: it keeps the decimals it was stamped with.
  return { ...data, rows, decimals: ONE_DECIMAL, npsDecimals: stampedDecimals }
}

const CONVERTERS: Record<string, (data: Record<string, unknown>) => Record<string, unknown>> = {
  'choice-bars': choiceBarsData,
  'share-donut': shareDonutData,
  'stacked-100-bars': categoricalData,
  'grouped-bars': categoricalData,
  'diverging-likert': divergingLikertData,
}

/** True when the kind is one this module re-reads at a tenth. */
export function convertsToOneDecimal(kind: string): boolean {
  return kind in CONVERTERS
}

function convertAlternatives(alternatives: unknown): unknown {
  if (!Array.isArray(alternatives)) {
    return alternatives
  }
  return alternatives.map((alternative) =>
    isRecord(alternative) && typeof alternative.kind === 'string' && isRecord(alternative.data)
      ? plotWithOneDecimalShares(alternative as unknown as LoosePlot)
      : alternative,
  )
}

/** One plot at one decimal, or the same object when it cannot be converted whole. */
export function plotWithOneDecimalShares<T extends { kind: string; data: Record<string, unknown> }>(plot: T): T {
  const convert = CONVERTERS[plot.kind]
  const alternatives = (plot as { alternatives?: unknown }).alternatives
  const convertedAlternatives = alternatives === undefined ? undefined : convertAlternatives(alternatives)
  let data: Record<string, unknown> = plot.data
  if (convert) {
    try {
      data = convert(plot.data)
    } catch (error) {
      if (!(error instanceof MissingExactShare)) {
        throw error
      }
    }
  }
  if (data === plot.data && convertedAlternatives === alternatives) {
    return plot
  }
  const next: T = { ...plot, data }
  if (alternatives !== undefined) {
    ;(next as { alternatives?: unknown }).alternatives = convertedAlternatives
  }
  return next
}

/** Every plot of a saved question (total, cut groups, lane cuts, alternatives) at one decimal. */
export function questionWithOneDecimalShares<T extends { plots: Record<string, { kind: string; data: Record<string, unknown> }> }>(
  question: T,
): T {
  const loose = question as unknown as LooseQuestion
  const plots: Record<string, LoosePlot> = {}
  for (const [id, plot] of Object.entries(loose.plots)) {
    plots[id] = plotWithOneDecimalShares(plot)
  }
  const next: LooseQuestion = { ...loose, plots }
  if (loose.cutPlot) {
    next.cutPlot = plotWithOneDecimalShares(loose.cutPlot)
  }
  if (loose.cutPlots) {
    next.cutPlots = loose.cutPlots.map((lane) => ({ ...lane, plot: plotWithOneDecimalShares(lane.plot) }))
  }
  return next as unknown as T
}
